import React, { useState, useEffect } from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  useMediaQuery,
  Fab,
  Box,
  Slide,
  Paper
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Event as EventIcon,
  CardGiftcard as ReferralIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useMobileQuickActions from './useMobileQuickActions';

interface MobileBottomNavigationProps {
  onQuickAction?: () => void;
  showFloatingAction?: boolean;
  hideOnScroll?: boolean;
}

const MobileBottomNavigation: React.FC<MobileBottomNavigationProps> = ({
  onQuickAction,
  showFloatingAction = true,
  hideOnScroll = true
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { actions } = useMobileQuickActions();
  
  // Локальное состояние
  const [value, setValue] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Определение активной вкладки на основе маршрута
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') {
      setValue(0);
    } else if (path.includes('/events')) {
      setValue(1);
    } else if (path.includes('/referral')) {
      setValue(2);
    } else if (path.includes('/analytics')) {
      setValue(3);
    } else if (path.includes('/settings') || path.includes('/profile')) {
      setValue(4);
    }
  }, [location.pathname]);

  // Скрытие навигации при прокрутке
  useEffect(() => {
    if (!hideOnScroll) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Прокрутка вниз - скрываем
        setIsVisible(false);
      } else {
        // Прокрутка вверх - показываем
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, hideOnScroll]);

  // Обработка навигации
  const handleNavigationChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    
    switch (newValue) {
      case 0:
        navigate('/dashboard');
        break;
      case 1:
        navigate('/events');
        break;
      case 2:
        navigate('/referral');
        break;
      case 3:
        navigate('/analytics');
        break;
      case 4:
        navigate('/settings');
        break;
      default:
        break;
    }
  };

  // Обработка быстрого действия
  const handleQuickAction = () => {
    if (onQuickAction) return onQuickAction();
    const first = actions?.[0];
    if (first?.route) navigate(first.route);
    else navigate('/events/create');
  };

  // Показываем только на мобильных устройствах
  if (!isMobile) {
    return null;
  }

  return (
    <>
      {/* Основная навигация */}
      <Slide direction="up" in={isVisible} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: '16px 16px 0 0',
            overflow: 'hidden'
          }}
        >
          <BottomNavigation
            value={value}
            onChange={handleNavigationChange}
            showLabels
            sx={{
              height: 70,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                padding: '6px 12px 8px',
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                }
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                '&.Mui-selected': {
                  fontSize: '0.75rem',
                }
              }
            }}
          >
            <BottomNavigationAction
              label={t('navigation.dashboard')}
              icon={<DashboardIcon />}
            />
            
            <BottomNavigationAction
              label={t('navigation.events')}
              icon={<EventIcon />}
            />
            
            <BottomNavigationAction
              label={t('navigation.referrals')}
              icon={<ReferralIcon />}
            />
            
            <BottomNavigationAction
              label={t('navigation.analytics')}
              icon={<AnalyticsIcon />}
            />
            
            <BottomNavigationAction
              label={t('navigation.settingsLabel')}
              icon={<SettingsIcon />}
            />
          </BottomNavigation>
        </Paper>
      </Slide>

      {/* Плавающая кнопка быстрого действия */}
      {showFloatingAction && (
        <Slide direction="up" in={isVisible} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: 'fixed',
              bottom: 85, // Над навигацией
              right: 16,
              zIndex: 1001
            }}
          >
            <Fab
              color="primary"
              onClick={handleQuickAction}
              sx={{
                width: 56,
                height: 56,
                boxShadow: theme.shadows[8],
                '&:hover': {
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <AddIcon />
            </Fab>
          </Box>
        </Slide>
      )}

      {/* Отступ снизу для контента */}
      <Box sx={{ height: 70 }} />
    </>
  );
};

export default MobileBottomNavigation; 